import { startTransition, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Reorder, useDragControls } from "framer-motion";
import { DragDropProvider } from "@dnd-kit/react";
import { useSortable } from "@dnd-kit/react/sortable";
import { AutoScroller } from "@dnd-kit/dom";
import { move } from "@dnd-kit/helpers";
import { Rnd } from "react-rnd";
import { toast } from "sonner";
import {
  NestDepoContext, NestDropContext, NestDropDot, NEST_KAPALI, parseNestId, otoKaydirmaKilidi, nestHedefCoz, nestHedefIzle, nestHedefSifirla,  setDragCountBadge, SURUKLE_ICINE_TASI_ACIK, useNestDeposu,
} from "../lib/nestDrop";
import { hizliEklentiler } from "../lib/surukleHizli";
import { crossBaslat, crossBitir, crossHedef, useCrossNest } from "../lib/crossWindowDrag";
import { Edit3, GripVertical, Maximize2, Minimize2, ArrowLeft, Link2, Unlink, CornerDownRight } from "lucide-react";
import { TaskCard } from "./TaskCard";
import { ResizeGrip } from "./ResizeGrip";
import { groupColorOf, hexToRgba } from "../lib/groupColors";

// AŞAMA 7 — Sürüklerken kenar otomatik kaydırma HIZI.
// Kullanıcı bu değeri /poc-surukleme sayfasında KENDİ FARESİYLE ONAYLADI
// ("çok güzel, hissi beğendim, istediğim buydu") — DEĞİŞTİRMEDEN ÖNCE SOR.
// Ölçüm: kenarda ~420 px/s, kenar bölgesinin başında ~120 px/s.
const AUTO_SCROLL_ACCELERATION = 8;

export const TASK_AUTO_SCROLL = AutoScroller.configure({
  acceleration: AUTO_SCROLL_ACCELERATION,
  threshold: { x: 0, y: 0.2 }, // kenar bölgesi = kabın yüksekliğinin %20'si
});
// AŞAMA 11 — yüzen pencerelerin kendi sağlayıcıları da AYNI kaydırma ayarını
// kullanır (hız değişmez).
export const windowAutoScrollPlugins = (defaults) => hizliEklentiler([
  ...defaults.filter((p) => p !== AutoScroller),
  TASK_AUTO_SCROLL,
]);

// Sürükleme sırasında imleç konumu (iş koluna bırakma hedefini bulmak için;
// dnd-kit olayları ekran koordinatı vermez). dnd-kit kendi dinleyicilerinde
// olayı durdurabildiği için YAKALAMA (capture) aşamasında dinlenir.
export const pointer = { x: null, y: null };
const trackPointer = (e) => { pointer.x = e.clientX; pointer.y = e.clientY; };
if (typeof window !== "undefined") {
  window.addEventListener("pointermove", trackPointer, true);
  window.addEventListener("pointerdown", trackPointer, true);
}

// AŞAMA 8 — Görev listesinin bulunduğu panelin SINIRI. Sabit değer YOK:
// listenin en yakın kaydırılabilir atası sınır kabul edilir; böylece sidebar,
// yüzen pencere ve tam ekran hallerinin hepsinde doğru çalışır.
const BOUNDS_ATTR = "data-sertex-task-bounds";
const findScrollBounds = (node) => {
  let el = node?.parentElement;
  while (el) {
    const s = window.getComputedStyle(el);
    if (s.overflowY === "auto" || s.overflowY === "scroll") return el;
    el = el.parentElement;
  }
  return null;
};
// Panel sınırlarının DIŞINDA mı? (sınır bulunamazsa asla "dışarıda" demeyiz)
const isOutsidePanel = (boundsEl, x, y) => {
  if (!boundsEl || x == null || y == null) return false;
  const r = boundsEl.getBoundingClientRect();
  return x < r.left || x > r.right || y < r.top || y > r.bottom;
};
// Bırakma noktasında bir iş kolu (kategori) çipi var mı? Varsa ESKİ davranış
// kazanır: kategoriye atama yapılır, kart dışarı TAŞINMAZ.
const hasCategoryTargetAt = (x, y) => {
  if (x == null || y == null || typeof document.elementsFromPoint !== "function") return false;
  return document.elementsFromPoint(x, y).some((n) => n?.closest?.("[data-cat-drop]"));
};
// Dışa alınan kartın yüzen pencere konumunu bırakma noktasına ayarlar.
export const placeDetachedAt = (id, x, y) => {
  const cx = Math.max(0, Math.min(window.innerWidth - 200, Math.round(x - 40)));
  const cy = Math.max(0, Math.min(window.innerHeight - 120, Math.round(y - 20)));
  saveDetachedGeom(id, { x: cx, y: cy });
  return { x: cx, y: cy };
};
// Yüzen kartın bırakıldığı nokta görev listesinin İÇİNDE mi? (geri koyma)
export const isInsideTaskPanel = (x, y) => {
  const el = document.querySelector(`[${BOUNDS_ATTR}]`);
  if (!el || x == null || y == null) return false;
  const r = el.getBoundingClientRect();
  return x >= r.left && x <= r.right && y >= r.top && y <= r.bottom;
};

// !!! FAZ 2B — SÜRÜKLE-BIRAK ile "İÇİNE TAŞIMA" (2. seçenek: kart sağ kenarında
// "＋İÇİNE" hedef noktası). Ortak tanımlar `lib/nestDrop.jsx` içinde.

/**
 * AŞAMA 7 — Görev listesi sıralama kabı (framer `Reorder.Group` yerine).
 * `children` bir FONKSİYONDUR: sürükleme sırasındaki canlı sırayı alır.
 * Sıralama iş mantığı değişmedi: bırakıldığında `onReorder(yeniDizi)` çağrılır.
 */
export const TaskSortableGroup = ({
  values, onReorder, className, children,
  onCategoryDragMove, onCategoryDrop, onDropOutside, onSubtaskReorder,
  onSubtaskDropOutside,
  // FAZ 2B — kartın/alt görevin ÜSTÜNE bırakma ("içine taşı").
  onDropIntoTask, invalidDropIdsFor, dragCountOf,
  // FAZ 5 — pencereler arası kapsam adı. Her görev paneli (sidebar ve her
  // iş kolu penceresi) AYRI kapsam olmalı; aynı adı paylaşanlar birbirinin
  // sürüklemesinde "+İÇİNE" noktası çizmez.
  kapsam = "sidebar",
}) => {  // AŞAMA 8 — panel sınırı + "dışarı çıkacak" görsel geri bildirimi.
  const rootRef = useRef(null);
  const boundsRef = useRef(null);
  const [outside, setOutside] = useState(false);
  useEffect(() => {
    if (!onDropOutside) return undefined;
    const el = findScrollBounds(rootRef.current);
    boundsRef.current = el;
    if (el) el.setAttribute(BOUNDS_ATTR, "1");
    return () => { if (el) el.removeAttribute(BOUNDS_ATTR); };
  }, [onDropOutside]);
  // `values` ya anahtar dizisi (satır anahtarları) ya da görev objesi dizisidir.
  // dnd-kit `move()` yalnızca KİMLİK dizisiyle çalışır; bu yüzden sıra daima
  // id dizisi üzerinde tutulur, dışa verirken orijinal öğelere geri eşlenir.
  const keyOf = (v) => (v && typeof v === "object" ? v.id : v);
  const byKey = Object.fromEntries(values.map((v) => [keyOf(v), v]));
  const [previewIds, setPreviewIds] = useState(() => values.map(keyOf));
  const draggingRef = useRef(false);
  // Sürükleme sırasındaki canlı sıra; bırakıldığında yan etki (onReorder)
  // state güncelleyicisinin İÇİNDEN değil buradan okunur (React uyarısı).
  const previewRef = useRef(values.map(keyOf));
  const [nest, setNest] = useState(NEST_KAPALI);
  // FAZ 3 — başka bir kapsamda (yüzen pencere) sürükleme varsa sidebar
  // listesi de kendi noktalarını çizer (pencere → sidebar taşıma).
  // FAZ 4 — hızlı modda nokta durumu depoda: sürükleme başında liste
  // yeniden çizilmez (yalnızca noktalar).
  const depo = useNestDeposu(kapsam);
  const disNest = useCrossNest(kapsam, !depo);
  const nestAktifMi = () => (depo ? depo.yerelAktif() : nest.aktif);
  const setOrder = (ids) => { previewRef.current = ids; setPreviewIds(ids); };
  // Sıra zaten aynıysa state yazılmaz → gereksiz tam liste render'ı olmaz.
  const sirayiSifirla = (ids) => {
    if (depo && ids.length === previewIds.length && ids.every((k, i) => k === previewIds[i])) {
      previewRef.current = ids;
      return;
    }
    setOrder(ids);
  };
  useEffect(() => {
    if (!draggingRef.current) setOrder(values.map(keyOf));
  }, [values]);
  const toItems = (ids) => ids.map((k) => byKey[k]).filter((v) => v !== undefined);
  const ordered = draggingRef.current ? toItems(previewIds) : values;

  return (
    <DragDropProvider
      plugins={(defaults) => hizliEklentiler([
        ...defaults.filter((p) => p !== AutoScroller),
        TASK_AUTO_SCROLL,
      ])}
      onDragStart={(event) => {
        draggingRef.current = true;
        nestHedefSifirla();
        sirayiSifirla(values.map(keyOf));
        startViewportEdgeScroll();
        // FAZ 2B — noktalar YALNIZCA sürükleme sırasında ve YALNIZCA geçerli
        // hedeflerde çizilir. Tek bir state güncellemesi (her karede DEĞİL).
        const src = event?.operation?.source;
        if (SURUKLE_ICINE_TASI_ACIK && onDropIntoTask) {
          // 60+ kartta noktaların tek karede basılması ~340 ms takılma
          // yapıyordu. `startTransition` ile bu render sürüklemeyi bloklamaz:
          // parmak/fare akıcı kalır, noktalar bir kare sonra görünür.
          const gecersiz = invalidDropIdsFor ? invalidDropIdsFor(src?.id, src?.data || {}) : null;
          if (depo) depo.yerelAyarla({ aktif: true, gecersiz });
          else startTransition(() => setNest({ aktif: true, gecersiz }));
          crossBaslat(kapsam, gecersiz);
          if (dragCountOf) {
            const n = dragCountOf(src?.id, src?.data || {});
            setDragCountBadge(n, src?.data?.kind === "subtask" ? "alt görev" : "görev");
          }
        }
      }}
      onDragMove={() => {
        if (onCategoryDragMove && pointer.x != null) {
          onCategoryDragMove({ x: pointer.x, y: pointer.y });
        }
        if (onDropOutside) {
          const out = isOutsidePanel(boundsRef.current, pointer.x, pointer.y)
            && !hasCategoryTargetAt(pointer.x, pointer.y);
          setOutside((prev) => (prev === out ? prev : out));
        }
      }}
      onDragOver={(event, manager) => {
        // FAZ 2B — hedef "İÇİNE" noktasıyken sıralama önizlemesi UYGULANMAZ
        // ve kenar otomatik kaydırma durdurulur (nokta yerinde kalsın).
        const nestHedef = nestHedefIzle(event, manager);
        otoKaydirmaKilidi(manager, !!nestHedef);
        if (nestHedef) return;
        // AŞAMA 9 — sürüklenen bir ALT GÖREV ise kart sıralamasına karışmaz;
        // yalnızca kendi kardeş grubunda yer değiştirir (yerel önizleme).
        if (event?.operation?.source?.data?.kind === "subtask") {
          if (onSubtaskReorder) onSubtaskReorder(event, true);
          return;
        }
        setOrder(move(previewRef.current, event));
        // İş kolu çipi vurgusu: dnd-kit onDragMove'u her karede vermeyebiliyor.
        if (onCategoryDragMove && pointer.x != null) {
          onCategoryDragMove({ x: pointer.x, y: pointer.y });
        }
      }}
      onDragEnd={(event, manager) => {
        draggingRef.current = false;
        otoKaydirmaKilidi(manager, false);
        setOutside(false);
        stopViewportEdgeScroll();
        // FAZ 2B — kullanıcının NİŞAN ALDIĞI "＋İÇİNE" noktası okunur.
        // FAZ 3 — yerel hedef yoksa PENCERELER ARASI hedefe bakılır (sidebar →
        // yüzen pencere). Hedef yoksa davranış eskisiyle aynı kalır.
        const nestAktif = nestAktifMi();
        const yerel = onDropIntoTask && !event?.canceled && nestAktif
          ? nestHedefCoz(event, manager)
          : null;
        const hedef = yerel
          || (onDropIntoTask && !event?.canceled && nestAktif ? crossHedef() : null);
        if (nestAktif) {
          if (depo) depo.yerelAyarla(NEST_KAPALI);
          else setNest(NEST_KAPALI);
        }
        crossBitir();
        setDragCountBadge(0);
        if (hedef) {
          const src = event?.operation?.source;
          if (src?.data?.kind === "subtask") {
            // yerel sıralama önizlemesini geri al, sonra taşımayı uygula
            if (onSubtaskReorder) onSubtaskReorder(event, false, true);
          } else {
            setOrder(values.map(keyOf));
          }
          onDropIntoTask(hedef, { id: src?.id, data: src?.data || {} });
          return;
        }
        if (event?.operation?.source?.data?.kind === "subtask") {
          // AŞAMA 9 (Adım 2) — alt görev panelin DIŞINA bırakıldıysa yüzen
          // pencereye taşınır; sıralama uygulanmaz (önizleme geri alınır).
          const src = event?.operation?.source;
          const disaridaMi = onSubtaskDropOutside
            && !hasCategoryTargetAt(pointer.x, pointer.y)
            && isOutsidePanel(boundsRef.current, pointer.x, pointer.y);
          if (disaridaMi) {
            if (onSubtaskReorder) onSubtaskReorder(event, false, true);
            onSubtaskDropOutside(src.id, src.data || {}, { x: pointer.x, y: pointer.y });
            return;
          }
          // KUSUR 1 (AŞAMA 9) — `canceled` durumunda da çağrılır: sürükleme
          // sırasında uygulanan YEREL önizleme ya kalıcı yazılır ya da geri
          // alınır. Aksi halde ekranda yeni sıra görünüp sunucuda eski
          // kalıyordu (yenilemede sıra ve numaralar geri dönüyordu).
          if (onSubtaskReorder) onSubtaskReorder(event, false, !!event?.canceled);
          return;
        }
        const sourceId = event?.operation?.source?.id;
        if (event?.canceled) { setOrder(values.map(keyOf)); return; }
        const onCat = hasCategoryTargetAt(pointer.x, pointer.y);
        // AŞAMA 8 — panel dışına bırakıldıysa (ve bir iş kolu çipi üstünde
        // değilse) kart dışa taşınır; yeniden sıralama YAPILMAZ.
        if (onDropOutside && !onCat && isOutsidePanel(boundsRef.current, pointer.x, pointer.y)) {
          setOrder(values.map(keyOf));
          onDropOutside(sourceId, { x: pointer.x, y: pointer.y });
          return;
        }
        if (onReorder) onReorder(toItems(previewRef.current));
        if (onCategoryDrop && pointer.x != null) {
          onCategoryDrop(sourceId, { x: pointer.x, y: pointer.y });
        }
      }}
    >
      <div
        ref={rootRef}
        className={`${className || ""}${outside ? " rounded-lg ring-1 ring-sertex-cyan/60" : ""}`}
        data-outside-drop={outside ? "true" : "false"}
      >
        <NestDepoContext.Provider value={depo}>
          <NestDropContext.Provider value={nest.aktif ? nest : disNest}>
            {children(ordered)}
          </NestDropContext.Provider>
        </NestDepoContext.Provider>
      </div>
    </DragDropProvider>
  );
};

export const ReorderableTaskCard = ({ task, index = 0, onDragToCategory, onDropToCategory, ...rest }) => {
  const { ref, handleRef, isDragSource } = useSortable({
    id: task.id, index, type: "task", accept: ["task"],
  });
  return (
    <div ref={ref} data-dragsource={isDragSource ? "true" : "false"} className="relative">
      <NestDropDot taskId={task.id} />
      <TaskCard task={task} {...rest} dragHandleRef={handleRef} />
    </div>
  );
};

// Task 4 — dışarı alınan görevin listedeki yerinde görünen kompakt yer tutucu.
export const DetachedPlaceholderCard = ({ task, displayNumber, onDock }) => (
  <div
    data-testid={`task-detached-placeholder-${task.id}`}
    className="rounded-lg border border-dashed border-sertex-cyan/40 bg-sertex-cyan/5 p-2.5 flex items-center gap-2"
  >
    <Maximize2 className="h-4 w-4 text-sertex-cyan shrink-0" />
    <div className="flex-1 min-w-0">
      <div className="hud-text text-sertex-cyan/90 truncate">
        {displayNumber ? `#${displayNumber} ` : ""}{task.title}
      </div>
      <div className="text-[10px] font-mono text-sertex-textMuted">Bu görev büyük pencerede açık</div>
    </div>
    <button
      onClick={onDock}
      data-testid={`task-redock-${task.id}`}
      title="Sidebar'a geri al"
      className="shrink-0 px-2 py-1 rounded-md border border-sertex-cyan/40 text-sertex-cyan hover:bg-sertex-cyan/15 hud-text flex items-center gap-1 transition-colors"
    >
      <Minimize2 className="h-3.5 w-3.5" /> GERİ AL
    </button>
  </div>
);

// Sıralama kabında yer tutucuyu (detached kart) sürüklemeden tutmak için.
export const ReorderablePlaceholder = ({ task, index = 0, ...rest }) => {
  const { ref } = useSortable({ id: task.id, index, disabled: true, type: "task", accept: ["task"] });
  return (
    <div ref={ref}>
      <DetachedPlaceholderCard task={task} {...rest} />
    </div>
  );
};

// GÖREV BAĞLAMA — Dış listede tek bir görevi temsil eden satır (value = key).
export const OuterTaskRow = ({ rowKey, index = 0, cardProps, detached, placeholderProps }) => {
  const { ref, handleRef, isDragSource } = useSortable({
    id: rowKey, index, disabled: !!detached, type: "task", accept: ["task"],
  });
  const { onDragToCategory, onDropToCategory, ...restCard } = cardProps || {};
  return (
    <div
      ref={ref}
      data-dragsource={isDragSource ? "true" : "false"}
      data-dndprobe={detached ? "off" : "on"}
      className="relative"
    >
      {!detached && <NestDropDot taskId={restCard?.task?.id || rowKey} />}
      {detached ? (
        <DetachedPlaceholderCard {...placeholderProps} />
      ) : (
        <TaskCard {...restCard} dragHandleRef={handleRef} />
      )}
    </div>
  );
};

// GÖREV BAĞLAMA — dışarı alınan grup penceresinde tek üye satırı (sürükle-sırala).
export const GroupWindowMemberRow = ({ task, cardProps }) => {
  const controls = useDragControls();
  return (
    <Reorder.Item value={task} dragListener={false} dragControls={controls} layout>
      <div className="flex items-start gap-1.5">
        <button
          onPointerDown={(e) => controls.start(e)}
          title="Sırala"
          data-testid={`group-window-drag-${task.id}`}
          className="cursor-grab touch-none p-1 mt-2 text-sertex-cyan/50 hover:text-sertex-cyan shrink-0"
        >
          <GripVertical className="h-4 w-4" />
        </button>
        <div className="flex-1 min-w-0">
          <TaskCard {...cardProps} detached />
        </div>
      </div>
    </Reorder.Item>
  );
};

// GÖREV BAĞLAMA — Personel Görevleri (team) için statik grup bloğu (sürükleme yok).
export const StaticTaskGroupBlock = ({ group, tasks, renderStaticMember, onEditGroup, onDissolve }) => {
  const doneCount = tasks.filter((t) => t.status === "done").length;
  const c = groupColorOf(group);
  return (
    <div
      className="rounded-xl border-2 border-sertex-cyan/40 bg-sertex-cyan/[0.04] p-1.5 space-y-1.5 shadow-[0_0_18px_rgba(34,211,238,0.08)]"
      style={c ? { borderColor: hexToRgba(c, 0.55), background: hexToRgba(c, 0.06), boxShadow: `0 0 18px ${hexToRgba(c, 0.14)}` } : undefined}
      data-testid={`task-group-${group.id}`}
    >
      <div className="flex items-center gap-1.5 px-1 py-0.5">
        <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ background: c || "rgb(var(--sx-accent-rgb))" }} />
        <Link2 className="h-3.5 w-3.5 text-sertex-cyan shrink-0" style={c ? { color: c } : undefined} />
        <span className="hud-text text-sertex-cyan flex-1 truncate tracking-wider" style={c ? { color: c } : undefined}>
          {group.name || "BAĞLI GÖREVLER"}
        </span>
        {group.show_progress && (
          <span
            className="hud-text px-1.5 py-0.5 rounded border border-sertex-cyan/40 text-sertex-cyan bg-sertex-cyan/10 whitespace-nowrap"
            style={c ? { color: c, borderColor: hexToRgba(c, 0.4), background: hexToRgba(c, 0.1) } : undefined}
            data-testid={`group-progress-${group.id}`}
          >
            {doneCount}/{tasks.length} tamamlandı
          </span>
        )}
        <button
          onClick={() => onEditGroup(group)}
          title="Grubu düzenle"
          data-testid={`group-edit-${group.id}`}
          className="p-1 text-sertex-textMuted hover:text-sertex-cyan transition-colors"
        >
          <Edit3 className="h-3.5 w-3.5" />
        </button>
        <button
          onClick={() => onDissolve(group)}
          title="Bağlantıyı çöz"
          data-testid={`group-dissolve-${group.id}`}
          className="p-1 text-rose-300/80 hover:text-rose-300 transition-colors"
        >
          <Unlink className="h-3.5 w-3.5" />
        </button>
      </div>
      <div className="space-y-2">{tasks.map((t) => renderStaticMember(t))}</div>
    </div>
  );
};


// GÖREV BAĞLAMA — Bağlı görevleri tek blok halinde gösteren grup kutusu.
// Blok başlığındaki tutamaçtan sürüklenince tüm üyeler birlikte taşınır;
// içteki Reorder.Group ile grup içi sıra da sürüklenerek değiştirilebilir.
export const TaskGroupBlock = ({ rowKey, index = 0, group, tasks, renderMember, onReorderGroup, onEditGroup, onDissolve, onDetach }) => {
  const { ref, handleRef } = useSortable({ id: rowKey, index, type: "task", accept: ["task"] });
  const doneCount = tasks.filter((t) => t.status === "done").length;
  const c = groupColorOf(group);
  return (
    <div ref={ref}>
      <div
        className="rounded-xl border-2 border-sertex-cyan/40 bg-sertex-cyan/[0.04] p-1.5 space-y-1.5 shadow-[0_0_18px_rgba(34,211,238,0.08)]"
        style={c ? { borderColor: hexToRgba(c, 0.55), background: hexToRgba(c, 0.06), boxShadow: `0 0 18px ${hexToRgba(c, 0.14)}` } : undefined}
        data-testid={`task-group-${group.id}`}
      >
        <div className="flex items-center gap-1.5 px-1 py-0.5">
          <button
            ref={handleRef}
            title="Grubu taşı (hepsi birlikte)"
            data-testid={`group-drag-${group.id}`}
            className="cursor-grab touch-none p-1 text-sertex-cyan/70 hover:text-sertex-cyan"
          >
            <GripVertical className="h-4 w-4" />
          </button>
          <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ background: c || "rgb(var(--sx-accent-rgb))" }} />
          <Link2 className="h-3.5 w-3.5 text-sertex-cyan shrink-0" style={c ? { color: c } : undefined} />
          <span className="hud-text text-sertex-cyan flex-1 truncate tracking-wider" style={c ? { color: c } : undefined}>
            {group.name || "BAĞLI GÖREVLER"}
          </span>
          {group.show_progress && (
            <span
              className="hud-text px-1.5 py-0.5 rounded border border-sertex-cyan/40 text-sertex-cyan bg-sertex-cyan/10 whitespace-nowrap"
              style={c ? { color: c, borderColor: hexToRgba(c, 0.4), background: hexToRgba(c, 0.1) } : undefined}
              data-testid={`group-progress-${group.id}`}
            >
              {doneCount}/{tasks.length} tamamlandı
            </span>
          )}
          <button
            onClick={() => onEditGroup(group)}
            title="Grubu düzenle"
            data-testid={`group-edit-${group.id}`}
            className="p-1 text-sertex-textMuted hover:text-sertex-cyan transition-colors"
          >
            <Edit3 className="h-3.5 w-3.5" />
          </button>
          <button
            onClick={() => onDetach(group)}
            title="Grubu dışarı al (büyük pencere)"
            data-testid={`group-detach-${group.id}`}
            className="p-1 text-sertex-textMuted hover:text-sertex-cyan transition-colors"
          >
            <Maximize2 className="h-3.5 w-3.5" />
          </button>
          <button
            onClick={() => onDissolve(group)}
            title="Bağlantıyı çöz"
            data-testid={`group-dissolve-${group.id}`}
            className="p-1 text-rose-300/80 hover:text-rose-300 transition-colors"
          >
            <Unlink className="h-3.5 w-3.5" />
          </button>
        </div>
        <TaskSortableGroup
          values={tasks}
          onReorder={(nt) => onReorderGroup(group.id, nt)}
          className="space-y-2"
        >
          {(ordered) => ordered.map((t, i) => renderMember(t, i)).filter(Boolean)}
        </TaskSortableGroup>
      </div>
    </div>
  );
};


// Task 4 (Görev Karşılaştırma Masası) — detach pencere konum/boyut + hangi
// görevlerin dışarı alındığı localStorage'da kalıcıdır (reload'da geri gelir).
const DETACHED_GEOM_KEY = "sertex_detached_task_geom_v1";
const loadDetachedGeom = () => {
  try { return JSON.parse(localStorage.getItem(DETACHED_GEOM_KEY) || "{}"); } catch { return {}; }
};
const saveDetachedGeom = (id, patch) => {
  try {
    const all = loadDetachedGeom();
    all[id] = { ...(all[id] || {}), ...patch };
    localStorage.setItem(DETACHED_GEOM_KEY, JSON.stringify(all));
  } catch (e) { console.warn("[TasksPanel.jsx] hata bastırıldı:", e); }
};

// AŞAMA 8 (düzeltme) — Yüzen kartın panelin ÜZERİNE gelebilmesi için
// `bounds="window"` kaldırıldı (kart sağdaki panele dayanıp duruyordu).
// Ekran dışına kaçmaması kendi clamp'imizle korunur: kartın en az bu kadarı
// görünür alanda kalır.
const MIN_VISIBLE = 140;
// Pencere ekran dışına ASLA tam çıkmaz: her kenarda en az MIN_VISIBLE kadarı
// görünür alanda kalır (ONAY 1 — dikeyde de 140 px, yataydakiyle aynı).
const clampWindowPos = (x, y, w = MIN_VISIBLE, h = MIN_VISIBLE) => ({
  x: Math.max(-(Math.max(0, w - MIN_VISIBLE)), Math.min(window.innerWidth - MIN_VISIBLE, Math.round(x))),
  y: Math.max(0, Math.min(window.innerHeight - MIN_VISIBLE, Math.round(y))),
});
// ONAY 2 — Yenilemede KAYDEDİLEN konum birebir geri yüklenir; yalnızca artık
// görünür alanda geçerli değilse (ör. ekran küçültülmüş) clamp uygulanır.
const restoreWindowPos = (saved, def, w, h) => {
  if (saved?.x == null || saved?.y == null) return clampWindowPos(def.x, def.y, w, h);
  const gecerli =
    saved.x <= window.innerWidth - MIN_VISIBLE &&
    saved.x + w >= MIN_VISIBLE &&
    saved.y >= 0 &&
    saved.y <= window.innerHeight - MIN_VISIBLE;
  return gecerli ? { x: saved.x, y: saved.y } : clampWindowPos(saved.x, saved.y, w, h);
};
// AŞAMA 10 — pencere üstünlüğü (en son etkileşilen üstte) ve yüzen pencere
// üzerine bırakma yardımcıları.
// AŞAMA 11 — pencere üstünlüğü. z değeri 62–119 arasında döner: bağlam
// menüsü (z 130) ve onay diyalogları (z 2000) HER ZAMAN pencerelerin üstünde
// kalır. (Önceden sınırsız artıyordu ve menü pencerenin arkasına düşüyordu.)
let windowZ = 62;
export const nextWindowZ = () => {
  windowZ = windowZ >= 119 ? 62 : windowZ + 1;
  return windowZ;
};
// AŞAMA 10/11 — Pencere üzerine bırakma hedefleri: hem alt görev hem GÖREV
// penceresi geçerli hedeftir.
const WINDOW_SEL = "[data-detached-subwindow],[data-detached-taskwindow]";
const windowIdOf = (el) =>
  el.getAttribute("data-detached-subwindow") || el.getAttribute("data-detached-taskwindow");
// İmlecin altındaki pencerenin id'si (kendisi hariç).
// NOT: Sürükleme sırasında imleç SÜRÜKLENEN pencerenin üzerindedir; bu yüzden
// `elementFromPoint` değil `elementsFromPoint` (tüm yığın) kullanılır.
export const windowUnderPointer = (haricId = null) => {
  if (pointer.x == null) return null;
  const yigin = document.elementsFromPoint(pointer.x, pointer.y) || [];
  for (const el of yigin) {
    const win = el.closest ? el.closest(WINDOW_SEL) : null;
    if (!win) continue;
    const id = windowIdOf(win);
    if (id && id !== haricId) return id;
  }
  return null;
};
export const setWindowDropHint = (winId) => {
  document.querySelectorAll(WINDOW_SEL).forEach((el) => {
    const hedef = windowIdOf(el) === winId;
    el.style.boxShadow = hedef ? "inset 0 0 0 2px rgba(34,211,238,0.7)" : "";
  });
};
// AŞAMA 11 — imleç, verilen öğenin bulunduğu pencerenin DIŞINDA mı?
export const isPointerOutsideWindow = (node) => {
  const win = node?.closest ? node.closest(WINDOW_SEL) : null;
  if (!win || pointer.x == null) return false;
  const r = win.getBoundingClientRect();
  return pointer.x < r.left || pointer.x > r.right || pointer.y < r.top || pointer.y > r.bottom;
};

// Panel üzerine gelindiğinde "buraya bıraksam girecek" geri bildirimi.
export const setPanelDropHint = (on) => {
  const el = document.querySelector(`[${BOUNDS_ATTR}]`);
  if (!el) return;
  el.style.boxShadow = on ? "inset 0 0 0 2px rgba(34,211,238,0.6)" : "";
  el.style.background = on ? "rgba(34,211,238,0.05)" : "";
};

// KUSUR 2 (AŞAMA 9) — Panelin alt/üst kenarı sık sık EKRAN DIŞINDA kalıyor
// (ör. kart açıkken panel rect.bottom = 1079, ekran 800). dnd-kit'in kenar
// bölgesi panelin RECT'ine göre hesaplandığı için gerçek fare o bölgeye hiç
// ulaşamıyor ve otomatik kaydırma çalışmıyordu. Bu yüzden GÖRÜNÜR ALANIN
// kenarına göre ek bir kaydırma çalıştırıyoruz; dnd-kit'in kendi bölgesiyle
// ÇAKIŞMAZ (panel tamamen ekrandaysa devreye girmez).
// Hız, kullanıcının onayladığı dnd-kit hissiyle aynı (~430 px/s).
// Kullanıcı bu değeri onayladı — değiştirmeden önce sor.
const VIEWPORT_SCROLL_SPEED = 430;
const VIEWPORT_EDGE = 120;
let viewportScrollRaf = null;
const startViewportEdgeScroll = () => {
  if (viewportScrollRaf) return;
  let last = performance.now();
  const tick = (now) => {
    const dt = Math.min(0.05, (now - last) / 1000);
    last = now;
    const el = document.querySelector(`[${BOUNDS_ATTR}]`);
    if (el && pointer.y != null) {
      const r = el.getBoundingClientRect();
      const visTop = Math.max(r.top, 0);
      const visBottom = Math.min(r.bottom, window.innerHeight);
      // dnd-kit'in kendi kenar bölgeleri (panel RECT'ine göre). Kendi
      // kaydırmamız YALNIZCA dnd-kit'in tetiklenmediği aralıkta çalışır,
      // böylece hız iki katına çıkmaz.
      const dndBottomEdge = r.bottom - r.height * 0.2;
      const dndTopEdge = r.top + r.height * 0.2;
      const edge = Math.min(VIEWPORT_EDGE, (visBottom - visTop) * 0.2);
      if (pointer.x >= r.left && pointer.x <= r.right) {
        if (pointer.y > visBottom - edge && pointer.y < dndBottomEdge) {
          el.scrollTop += VIEWPORT_SCROLL_SPEED * dt;
        } else if (pointer.y < visTop + edge && pointer.y > dndTopEdge) {
          el.scrollTop -= VIEWPORT_SCROLL_SPEED * dt;
        }
      }
    }
    viewportScrollRaf = requestAnimationFrame(tick);
  };
  viewportScrollRaf = requestAnimationFrame(tick);
};
const stopViewportEdgeScroll = () => {  if (viewportScrollRaf) cancelAnimationFrame(viewportScrollRaf);
  viewportScrollRaf = null;
};


export const DetachedTaskWindow = ({ task, index = 0, onDock, children }) => {
  const DEF_W = Math.min(640, window.innerWidth - 80);
  const DEF_H = Math.min(680, window.innerHeight - 100);
  const saved = loadDetachedGeom()[task.id] || {};
  const [z, setZ] = useState(60);
  const [size, setSize] = useState({
    width: saved.width ?? DEF_W,
    height: saved.height ?? DEF_H,
  });
  const [pos, setPos] = useState(() =>
    restoreWindowPos(
      saved,
      {
        x: Math.round(window.innerWidth / 2 - DEF_W / 2) + index * 34,
        y: Math.round(window.innerHeight / 2 - DEF_H / 2) + index * 34,
      },
      saved.width ?? DEF_W,
      saved.height ?? DEF_H
    )
  );
  // FAZ B — bu görev için "konuma git" isteği gelirse pencere ÖNE getirilir
  // (yeni kopya açılmaz).
  // FAZ C — düzen uygulanırken kaydedilen z-sırası geri yüklenir.
  useEffect(() => {
    const onJump = (ev) => {
      if (ev?.detail?.task_id === task.id) setZ(nextWindowZ());
    };
    const onLayoutZ = (ev) => {
      const v = ev?.detail?.[`task:${task.id}`];
      if (v != null) setZ(v);
    };
    window.addEventListener("sertex:task-jump", onJump);
    window.addEventListener("sertex:layout-z", onLayoutZ);
    return () => {
      window.removeEventListener("sertex:task-jump", onJump);
      window.removeEventListener("sertex:layout-z", onLayoutZ);
    };
  }, [task.id]);
  return createPortal(
    <Rnd
      position={pos}
      size={size}
      minWidth={320}
      minHeight={240}
      dragHandleClassName={`detached-task-header-${task.id}`}
      onMouseDown={() => setZ(nextWindowZ())}
      onDrag={() => {
        // Bırakma hedefi panelse kullanıcıya göster.
        setPanelDropHint(isInsideTaskPanel(pointer.x, pointer.y));
      }}
      onDragStop={(e, d) => {
        setPanelDropHint(false);
        // AŞAMA 8 — yüzen kart görev listesinin İÇİNE bırakıldıysa geri döner
        // (placeholder'ın durduğu ESKİ yerine oturur). Ölçüt İMLEÇ konumudur:
        // kullanıcı "nereye bıraktıysam" hissiyle örtüşen davranış budur.
        if (isInsideTaskPanel(pointer.x, pointer.y)) { onDock(); return; }
        const p = clampWindowPos(d.x, d.y, size.width, size.height);
        setPos(p); saveDetachedGeom(task.id, p);
      }}
      onResizeStop={(e, dir, ref, delta, p) => {
        const w = parseInt(ref.style.width, 10) || DEF_W;
        const h = parseInt(ref.style.height, 10) || DEF_H;
        setSize({ width: w, height: h });
        setPos(p);
        saveDetachedGeom(task.id, { x: p.x, y: p.y, width: w, height: h });
      }}
      style={{ zIndex: z }}
      resizeHandleComponent={{ bottomRight: <ResizeGrip testId={`task-${task.id}`} /> }}
      data-testid={`detached-task-window-${task.id}`}
    >
      <div
        data-detached-taskwindow={task.id}
        className="h-full flex flex-col glass-panel rounded-lg overflow-hidden border border-sertex-cyan/50 shadow-[0_8px_40px_rgba(0,0,0,0.6)] transition-colors"
      >
        <div
          className={`detached-task-header-${task.id} flex items-center gap-2 px-3 py-2 border-b border-sertex-cyan/30 bg-sertex-cyan/10 cursor-move select-none`}
        >
          <Maximize2 className="h-4 w-4 text-sertex-cyan shrink-0" />
          <div className="flex-1 min-w-0 hud-text text-sertex-cyan truncate">
            GÖREV · {task.title}
          </div>
          <button
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => { e.stopPropagation(); onDock(); }}
            data-testid={`detached-task-dock-${task.id}`}
            title="Sidebar'a geri al"
            className="shrink-0 px-2 py-1 rounded-md border border-sertex-cyan/50 text-sertex-cyan hover:bg-sertex-cyan/20 hud-text flex items-center gap-1 transition-colors"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> SIDEBAR'A GERİ AL
          </button>
        </div>
        <div className="flex-1 overflow-y-auto scrollbar-sertex p-3">
          {children}
        </div>
      </div>
    </Rnd>,
    document.body
  );
};

// AŞAMA 9 (Adım 2) — dışarı alınan ALT GÖREV için yüzen pencere.
// Geometri anahtarı `sub:<altGörevId>` biçimindedir (görev anahtarıyla çakışmaz).
export const DetachedSubtaskWindow = ({ subId, title, taskTitle, index = 0, onDock, children }) => {
  const DEF_W = Math.min(520, window.innerWidth - 80);
  const DEF_H = Math.min(420, window.innerHeight - 120);
  const geomKey = `sub:${subId}`;
  const saved = loadDetachedGeom()[geomKey] || {};
  const [size, setSize] = useState({ width: saved.width ?? DEF_W, height: saved.height ?? DEF_H });
  // AŞAMA 10 — en son etkileşilen pencere üstte kalır.
  const [z, setZ] = useState(62);
  // FAZ C — kaydedilen z-sırasını geri yükle.
  useEffect(() => {
    const onLayoutZ = (ev) => {
      const v = ev?.detail?.[`subtask:${subId}`];
      if (v != null) setZ(v);
    };
    window.addEventListener("sertex:layout-z", onLayoutZ);
    return () => window.removeEventListener("sertex:layout-z", onLayoutZ);
  }, [subId]);
  const [pos, setPos] = useState(() =>
    restoreWindowPos(
      saved,
      {
        x: Math.round(window.innerWidth / 2 - DEF_W / 2) + index * 28,
        y: Math.round(window.innerHeight / 2 - DEF_H / 2) + index * 28,
      },
      saved.width ?? DEF_W,
      saved.height ?? DEF_H
    )
  );
  return createPortal(
    <Rnd
      position={pos}
      size={size}
      minWidth={280}
      minHeight={180}
      dragHandleClassName={`detached-subtask-header-${subId}`}
      onMouseDown={() => setZ(nextWindowZ())}
      onDrag={() => {
        setPanelDropHint(isInsideTaskPanel(pointer.x, pointer.y));
        // AŞAMA 10 (kural 6) — hedef ebeveyn penceresi vurgulanır.
        setWindowDropHint(windowUnderPointer(subId));
      }}
      onDragStop={(e, d) => {
        setPanelDropHint(false);
        setWindowDropHint(null);
        // AŞAMA 10 (kural 5) — Neural Link paneline VEYA başka bir alt görev
        // penceresine bırakılırsa eski ebeveyn ve indeksine döner.
        if (isInsideTaskPanel(pointer.x, pointer.y) || windowUnderPointer(subId)) { onDock(); return; }
        const p = clampWindowPos(d.x, d.y, size.width, size.height);
        setPos(p); saveDetachedGeom(geomKey, p);
      }}
      onResizeStop={(e, dir, ref, delta, p) => {
        const w = parseInt(ref.style.width, 10) || DEF_W;
        const h = parseInt(ref.style.height, 10) || DEF_H;
        setSize({ width: w, height: h });
        setPos(p);
        saveDetachedGeom(geomKey, { x: p.x, y: p.y, width: w, height: h });
      }}
      style={{ zIndex: z }}
      resizeHandleComponent={{ bottomRight: <ResizeGrip testId={`subtask-${subId}`} /> }}
      data-testid={`detached-subtask-window-${subId}`}
    >
      <div
        data-detached-subwindow={subId}
        className="h-full flex flex-col glass-panel rounded-lg overflow-hidden border border-violet-400/50 shadow-[0_8px_40px_rgba(0,0,0,0.6)] transition-colors"
      >
        <div className={`detached-subtask-header-${subId} flex items-center gap-2 px-3 py-2 border-b border-violet-400/30 bg-violet-500/10 cursor-move select-none`}>
          <CornerDownRight className="h-4 w-4 text-violet-300 shrink-0" />
          <div className="flex-1 min-w-0 hud-text text-violet-200 truncate" title={`${taskTitle || ""} · ${title || ""}`}>
            ALT GÖREV · {title}
          </div>
          <button
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => { e.stopPropagation(); onDock(); }}
            data-testid={`detached-subtask-dock-${subId}`}
            title="Listeye geri al"
            className="shrink-0 px-2 py-1 rounded-md border border-violet-400/50 text-violet-200 hover:bg-violet-500/20 hud-text flex items-center gap-1 transition-colors"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> LİSTEYE GERİ AL
          </button>
        </div>
        <div className="flex-1 overflow-y-auto scrollbar-sertex p-3">
          {children}
        </div>
      </div>
    </Rnd>,
    document.body
  );
};

// GÖREV BAĞLAMA — dışarı alınan bağlı grup için localStorage anahtarı.
export const DetachedGroupWindow = ({ group, doneCount = 0, total = 0, index = 0, onDock, children }) => {
  const DEF_W = Math.min(680, window.innerWidth - 80);
  const DEF_H = Math.min(720, window.innerHeight - 100);
  const geomKey = `group:${group.id}`;
  const saved = loadDetachedGeom()[geomKey] || {};
  const [size, setSize] = useState({
    width: saved.width ?? DEF_W,
    height: saved.height ?? DEF_H,
  });
  const [pos, setPos] = useState(() =>
    restoreWindowPos(
      saved,
      {
        x: Math.round(window.innerWidth / 2 - DEF_W / 2) + index * 34,
        y: Math.round(window.innerHeight / 2 - DEF_H / 2) + index * 34,
      },
      saved.width ?? DEF_W,
      saved.height ?? DEF_H
    )
  );
  return createPortal(
    <Rnd
      position={pos}
      size={size}
      minWidth={340}
      minHeight={260}
      bounds="window"
      dragHandleClassName={`detached-group-header-${group.id}`}
      onDragStop={(e, d) => { setPos({ x: d.x, y: d.y }); saveDetachedGeom(geomKey, { x: d.x, y: d.y }); }}
      onResizeStop={(e, dir, ref, delta, p) => {
        const w = parseInt(ref.style.width, 10) || DEF_W;
        const h = parseInt(ref.style.height, 10) || DEF_H;
        setSize({ width: w, height: h });
        setPos(p);
        saveDetachedGeom(geomKey, { x: p.x, y: p.y, width: w, height: h });
      }}
      style={{ zIndex: 60 }}
      resizeHandleComponent={{ bottomRight: <ResizeGrip testId={`group-${group.id}`} /> }}
      data-testid={`detached-group-window-${group.id}`}
    >
      <div className="h-full flex flex-col glass-panel rounded-lg overflow-hidden border-2 border-sertex-cyan/50 shadow-[0_8px_40px_rgba(0,0,0,0.6)]">
        <div
          className={`detached-group-header-${group.id} flex items-center gap-2 px-3 py-2 border-b border-sertex-cyan/30 bg-sertex-cyan/10 cursor-move select-none`}
        >
          <Link2 className="h-4 w-4 text-sertex-cyan shrink-0" />
          <div className="flex-1 min-w-0 hud-text text-sertex-cyan truncate">
            {group.name || "BAĞLI GÖREVLER"}
          </div>
          {group.show_progress && (
            <span className="hud-text px-1.5 py-0.5 rounded border border-sertex-cyan/40 text-sertex-cyan bg-sertex-cyan/10 whitespace-nowrap shrink-0">
              {doneCount}/{total} tamamlandı
            </span>
          )}
          <button
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => { e.stopPropagation(); onDock(); }}
            data-testid={`detached-group-dock-${group.id}`}
            title="Sidebar'a geri al"
            className="shrink-0 px-2 py-1 rounded-md border border-sertex-cyan/50 text-sertex-cyan hover:bg-sertex-cyan/20 hud-text flex items-center gap-1 transition-colors"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> SIDEBAR'A GERİ AL
          </button>
        </div>
        <div className="flex-1 overflow-y-auto scrollbar-sertex p-3 space-y-2">
          {children}
        </div>
      </div>
    </Rnd>,
    document.body
  );
};

// GÖREV BAĞLAMA — grup dışarı alındığında sidebar'da kalan yer tutucu.
export const GroupDetachedRow = ({ rowKey, index = 0, group, count, onDock }) => {
  const { ref } = useSortable({ id: rowKey, index, disabled: true, type: "task", accept: ["task"] });
  return (
  <div ref={ref}>
    <div
      className="rounded-xl border-2 border-dashed border-sertex-cyan/40 bg-sertex-cyan/5 p-2.5 flex items-center gap-2"
      data-testid={`task-group-detached-${group.id}`}
    >
      <Maximize2 className="h-4 w-4 text-sertex-cyan shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="hud-text text-sertex-cyan/90 truncate flex items-center gap-1">
          <Link2 className="h-3 w-3 shrink-0" /> {group.name || "BAĞLI GÖREVLER"} ({count})
        </div>
        <div className="text-[10px] font-mono text-sertex-textMuted">Bu grup büyük pencerede açık</div>
      </div>
      <button
        onClick={onDock}
        data-testid={`group-redock-${group.id}`}
        title="Sidebar'a geri al"
        className="shrink-0 px-2 py-1 rounded-md border border-sertex-cyan/40 text-sertex-cyan hover:bg-sertex-cyan/15 hud-text flex items-center gap-1 transition-colors"
      >
        <Minimize2 className="h-3.5 w-3.5" /> GERİ AL
      </button>
    </div>
  </div>
  );
};

